import axios from 'axios';
import { IService, ServiceType } from '../utils/entities/interface';
import { get, post } from './https'; // Make sure to adjust the import for the `get` function

interface Pagination {
  totalData: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ServiceListResponse {
  status: boolean;
  data: IService[];
  count: number;
  pagination: Pagination;
}

const GETServices = async ({
  page = 1,
  limit = 10,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<ServiceListResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/service/list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  // Fetch the response
  const response = await post(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch services');
  }
};

const GETSingleService = async (serviceId: string): Promise<IService> => {
  const url = `/api/v1/admin/service/${serviceId}`;

  // Fetch the response
  const response = await post(url);

  if (response.status === 200 && response.data.status) {
    return response.data.data;
  } else {
    throw new Error('Failed to fetch the service');
  }
};

// Error handling utility
interface CustomError extends Error {
  message: string;
}

interface AddServiceProps {
  name: string;
  description: string[];
  terms: string[];
  icon: string;
  category: 'BASIC' | 'GAS_CHG' | 'COPPER_PIPING' | 'AMC' | 'COMM_AC';
  banner_images: {
    type: string;
    url: string;
  }[];
}

const createNewService = async ({
  name,
  description,
  terms,
  category,
  icon,
  banner_images,
}: AddServiceProps) => {
  const key = name.toUpperCase().replace(/\s+/g, '_'); // Generate key from name
  // Helper function to check if a string is valid (non-empty and not just whitespace)
  const isValidString = (str: string) =>
    typeof str === 'string' && str.trim().length > 0;

  // Filter invalid terms
  const validTerms = terms.filter(isValidString);
  const validDescriptions = description.filter(isValidString);
  const newService = {
    name,
    description: validDescriptions,
    terms: validTerms,
    category,
    key,
    icon,
    banner_images,
  };

  console.log('newService', newService);
  try {
    const response = await post('/api/v1/admin/service/create', newService);
    if (response.status === 201) {
      return 201; // Success, return response data
    } else {
      throw new Error(response.statusText || 'Failed to create service');
    }
  } catch (error) {
    const err = error as CustomError;
    console.error('Error in createNewService:', err.message);
    throw new Error(
      err.message || 'An error occurred while creating the service',
    );
  }
};

interface EditServiceProps {
  serviceId: string;
  name: string;
  description: string[];
  terms: string[];
  icon: string;
  category: 'BASIC' | 'GAS_CHG' | 'COPPER_PIPING' | 'AMC' | 'COMM_AC';
  banner_images: {
    type: string;
    url: string;
  }[];
}
const EditService = async ({
  serviceId,
  name,
  description,
  terms,
  icon,
  category,
  banner_images,
}: EditServiceProps) => {
  const url = `/api/v1/admin/service/edit/${serviceId}`;
  const key = name.toUpperCase().replace(/\s+/g, '_');

  // Helper function to check if a string is valid (non-empty and not just whitespace)
  const isValidString = (str: string) =>
    typeof str === 'string' && str.trim().length > 0;

  // Filter invalid terms
  const validTerms = terms.filter(isValidString);
  const validDescriptions = description.filter(isValidString);

  const result = await post(url, {
    name,
    description: validDescriptions,
    terms: validTerms,
    icon,
    category,
    key,
    banner_images,
  });
  if (result.status == 201) {
    return 201;
  } else {
    throw Error('Failed to Edit Service');
  }
};

const GetPresignedUrl = async (
  fileName: string,
  fileType: string,
  type: number,
): Promise<string> => {
  console.log('FILENAMEINSIDESERVICE', fileName);
  console.log('TYPEINSIDESERVICE', fileType, type);
  const response = await get(
    `/api/v1/admin/generate-presigned-url?fileName=${fileName}&fileType=${fileType}&type=${type}`,
  );

  console.log('response', response);

  if (response.status == 200) {
    return response.data.presignedUrl;
  } else {
    throw new Error('Failed to get presigned URL');
  }
};

const UploadImageToS3 = async (
  file: File,
  preSignUrl: string,
): Promise<string> => {
  try {
    const response = await axios.put(preSignUrl, file, {
      headers: {
        'Content-Type': file.type, // Ensure the Content-Type matches the file type
      },
    });

    // Return the public URL (without query parameters)
    return preSignUrl.split('?')[0];
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};
export {
  GETServices,
  createNewService,
  GETSingleService,
  GetPresignedUrl,
  EditService,
  UploadImageToS3,
};
